import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Lightbulb } from "lucide-react";
import { api, fmtDate } from "../../lib/api";
import { useReader } from "../../context/ReaderContext";

const TONE = { relevant: "bg-emerald-100 text-emerald-900", possible: "bg-sky-100 text-sky-900", need_more: "bg-amber-100 text-amber-950", undetermined: "bg-slate-200 text-slate-800" };

const Row = ({ label, children, testId }) => (children ? <div data-testid={testId}><dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</dt><dd className="mt-1 whitespace-pre-line text-[15px]">{children}</dd></div> : null);

const Personal = ({ r }) => {
  const d = r.details || {};
  const when = [d.legal_effective_date && `stupa na snagu ${fmtDate(d.legal_effective_date)}`, d.applies_from && `od ${fmtDate(d.applies_from)}`, d.applies_to && `do ${fmtDate(d.applies_to)}`].filter(Boolean).join(", ");
  return (
    <div className="mt-5 border-t border-pulse/30 pt-5" data-testid="personal-applicability">
      <span className={`inline-block rounded-sm px-2 py-1 text-xs font-bold ${TONE[r.status]}`} data-testid="applicability-status">{r.label}</span>
      <p className="mt-2 text-[15px]" data-testid="applicability-explanation">{r.explanation}</p>
      {!r.has_profile && <p className="mt-2 text-sm"><Link to="/moj-balkan-info" className="font-semibold underline" data-testid="applicability-setup-link">Odaberi svoje podatke</Link> da vidiš da li se ovo moguće odnosi na tebe.</p>}
      {r.details && (
        <dl className="mt-4 grid gap-4">
          <Row label="Uslovi" testId="app-conditions">{d.conditions}</Row>
          <Row label="Izuzeci" testId="app-exceptions">{d.exceptions}</Row>
          <Row label="Kada se primjenjuje" testId="app-when">{[d.policy_status_label, when].filter(Boolean).join(" · ")}</Row>
          <Row label="Praktični koraci" testId="app-next-steps">{d.next_steps}</Row>
          <Row label="Nadležni organ" testId="app-authority">{d.authority_name && (d.authority_url ? <a href={d.authority_url} target="_blank" rel="noopener noreferrer nofollow" className="underline">{d.authority_name} <ExternalLink className="inline h-3 w-3" /></a> : d.authority_name)}</Row>
          {r.sources?.length > 0 && <Row label="Izvor" testId="app-sources">{r.sources.map((s, i) => <span key={i} className="block">{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="underline">{s.title}</a> : s.title}{s.publisher && ` – ${s.publisher}`}</span>)}</Row>}
          <Row label="Ograničenja dokaza" testId="app-limits">{d.evidence_limitations}</Row>
          <p className="text-xs text-muted-foreground" data-testid="app-verified">Pouzdanost: {d.confidence_label}{d.last_verified_at && ` · posljednja provjera redakcije: ${fmtDate(d.last_verified_at)}`} · <a href="#article-body-start" className="underline">Pročitaj cijeli članak</a></p>
        </dl>
      )}
      <p className="mt-4 text-xs text-muted-foreground" data-testid="applicability-disclaimer">{r.disclaimer}</p>
    </div>
  );
};

export const WhatItMeans = ({ a }) => {
  const { reader, guest } = useReader();
  const [r, setR] = useState(null);
  useEffect(() => {
    if (reader === null) return;
    api.post(`/personalization/applicability/${a.slug}`, { profile: reader ? null : guest?.profile || null }).then((x) => setR(x.data)).catch(() => setR(null));
  }, [a.slug, reader, guest]);
  if (!a.what_it_means_html && !r?.details) return null;
  return (
    <aside className="mb-10 border-l-4 border-pulse bg-pulse/[0.06] p-6" aria-labelledby="wim-h" data-testid="sta-ovo-znaci-box">
      <h2 id="wim-h" className="flex items-center gap-2 font-display text-xl font-semibold text-ink"><Lightbulb className="h-5 w-5 text-pulse" /> Šta ovo znači za mene?</h2>
      {a.what_it_means_html && <div className="prose-news mt-3 !text-base" dangerouslySetInnerHTML={{ __html: a.what_it_means_html }} />}
      {r && <Personal r={r} />}
    </aside>
  );
};
